import __var_0__ from '@stdlib/utils/define-nonenumerable-read-only-property';
import __var_1__ from '@stdlib/utils/define-nonenumerable-read-only-accessor';
import __var_2__ from '@stdlib/utils/define-nonenumerable-read-write-accessor';
import __var_3__ from '@stdlib/assert/is-positive-number';
import __var_4__ from '@stdlib/assert/is-plain-object';
import __var_5__ from '@stdlib/assert/is-function';
import __var_6__ from '@stdlib/assert/has-own-property';
import __var_7__ from '@stdlib/utils/constant-function';
import __var_8__ from '@stdlib/utils/noop';
import __var_9__ from '@stdlib/random/base/mt19937';
import __var_10__ from '@stdlib/math/base/assert/is-nan';
import __var_11__ from '@stdlib/array/to-json';
import __var_12__ from './poisson.js';
/**

*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //
var setReadOnly = __var_0__;
var setReadOnlyAccessor = __var_1__;
var setReadWriteAccessor = __var_2__;
var isPositive = __var_3__.isPrimitive;
var isObject = __var_4__;
var isFunction = __var_5__;
var hasOwnProp = __var_6__;
var constantFunction = __var_7__;
var noop = __var_8__;
var randu = __var_9__.factory;
var isnan = __var_10__;
var typedarray2json = __var_11__;
var poisson0 = __var_12__;


// MAIN //

/**
* Returns a pseudorandom number generator for generating Poisson distributed random numbers.
*
* @param {PositiveNumber} [lambda] - mean
* @param {Options} [options] - function options
* @param {PRNG} [options.prng] - pseudorandom number generator which generates uniformly distributed pseudorandom numbers
* @param {PRNGSeedMT19937} [options.seed] - pseudorandom number generator seed
* @param {PRNGStateMT19937} [options.state] - pseudorandom number generator state
* @param {boolean} [options.copy=true] - boolean indicating whether to copy a provided pseudorandom number generator state
* @throws {TypeError} `lambda` must be a positive number
* @throws {TypeError} options argument must be an object
* @throws {TypeError} must provide valid options
* @throws {Error} must provide a valid state
* @returns {PRNG} pseudorandom number generator
*
* @example
* var poisson = factory( 5.0 );
* var v = poisson();
* // returns <number>
*
* @example
* var poisson = factory( 8.0, {
*     'seed': 297
* });
* var v = poisson();
* // returns <number>
*
* @example
* var poisson = factory();
* var v = poisson( 0.5 );
* // returns <number>
*/
function factory() {
	var lambda;
	var opts;
	var rand;
	var prng;

	if ( arguments.length === 0 ) {
		rand = randu();
	} else if (
		arguments.length === 1 &&
		isObject( arguments[ 0 ] )
	) {
		opts = arguments[ 0 ];
		if ( hasOwnProp( opts, 'prng' ) ) {
			if ( !isFunction( opts.prng ) ) {
				throw new TypeError( 'invalid option. `prng` option must be a pseudorandom number generator function. Option: `' + opts.prng + '`.' );
			}
			rand = opts.prng;
		} else {
			rand = randu( opts );
		}
	} else {
		lambda = arguments[ 0 ];
		if ( !isPositive( lambda ) ) {
			throw new TypeError( 'invalid argument. First argument must be a positive number. Value: `' + lambda + '`.' );
		}
		if ( arguments.length > 1 ) {
			opts = arguments[ 1 ];
			if ( !isObject( opts ) ) {
				throw new TypeError( 'invalid argument. Options argument must be an object. Value: `' + opts + '`.' );
			}
			if ( hasOwnProp( opts, 'prng' ) ) {
				if ( !isFunction( opts.prng ) ) {
					throw new TypeError( 'invalid option. `prng` option must be a pseudorandom number generator function. Option: `' + opts.prng + '`.' );
				}
				rand = opts.prng;
			} else {
				rand = randu( opts );
			}
		} else {
			rand = randu();
		}
	}
	if ( lambda === void 0 ) {
		prng = poisson2;
	} else {
		prng = poisson1;
	}
	setReadOnly( prng, 'NAME', 'poisson' );

	// If we are provided an "external" PRNG, we don't support getting or setting PRNG state, as we'd need to check for compatible state value types, etc, entailing considerable complexity.
	if ( opts && opts.prng ) {
		setReadOnly( prng, 'seed', null );
		setReadOnly( prng, 'seedLength', null );
		setReadWriteAccessor( prng, 'state', constantFunction( null ), noop );
		setReadOnly( prng, 'stateLength', null );
		setReadOnly( prng, 'byteLength', null );
		setReadOnly( prng, 'toJSON', constantFunction( null ) );
		setReadOnly( prng, 'PRNG', rand );
	} else {
		setReadOnlyAccessor( prng, 'seed', getSeed );
		setReadOnlyAccessor( prng, 'seedLength', getSeedLength );
		setReadWriteAccessor( prng, 'state', getState, setState );
		setReadOnlyAccessor( prng, 'stateLength', getStateLength );
		setReadOnlyAccessor( prng, 'byteLength', getStateSize );
		setReadOnly( prng, 'toJSON', toJSON );
		setReadOnly( prng, 'PRNG', rand );
		rand = rand.normalized;
	}
	return prng;

	/**
	* Returns the PRNG seed.
	*
	* @private
	* @returns {PRNGSeedMT19937} seed
	*/
	function getSeed() {
		return rand.seed;
	}

	/**
	* Returns the PRNG seed length.
	*
	* @private
	* @returns {PositiveInteger} seed length
	*/
	function getSeedLength() {
		return rand.seedLength;
	}

	/**
	* Returns the PRNG state length.
	*
	* @private
	* @returns {PositiveInteger} state length
	*/
	function getStateLength() {
		return rand.stateLength;
	}

	/**
	* Returns the PRNG state size (in bytes).
	*
	* @private
	* @returns {PositiveInteger} state size (in bytes)
	*/
	function getStateSize() {
		return rand.byteLength;
	}

	/**
	* Returns the current pseudorandom number generator state.
	*
	* @private
	* @returns {PRNGStateMT19937} current state
	*/
	function getState() {
		return rand.state;
	}

	/**
	* Sets the pseudorandom number generator state.
	*
	* @private
	* @param {PRNGStateMT19937} s - generator state
	* @throws {Error} must provide a valid state
	*/
	function setState( s ) {
		rand.state = s;
	}

	/**
	* Serializes the pseudorandom number generator as a JSON object.
	*
	* ## Notes
	*
	* -   `JSON.stringify()` implicitly calls this method when stringifying a PRNG.
	*
	* @private
	* @returns {Object} JSON representation
	*/
	function toJSON() {
		var out = {};
		out.type = 'PRNG';
		out.name = prng.NAME;
		out.state = typedarray2json( rand.state );
		if ( lambda === void 0 ) {
			out.params = [];
		} else {
			out.params = [ lambda ];
		}
		return out;
	}

	/**
	* Returns a pseudorandom number drawn from a Poisson distribution.
	*
	* @private
	* @returns {NonNegativeInteger} pseudorandom number
	*
	* @example
	* var v = poisson1();
	* // returns <number>
	*/
	function poisson1() {
		return poisson0( rand, lambda );
	}

	/**
	* Returns a pseudorandom number drawn from a Poisson distribution.
	*
	* @private
	* @param {PositiveNumber} lambda - mean
	* @returns {NonNegativeInteger} pseudorandom number
	*
	* @example
	* var v = poisson2( 0.5 );
	* // returns <number>
	*
	* @example
	* var v = poisson2( NaN );
	* // returns NaN
	*
	* @example
	* var v = poisson2( -1.0 );
	* // returns NaN
	*/
	function poisson2( lambda ) {
		if (
			isnan( lambda ) ||
			lambda <= 0.0
		) {
			return NaN;
		}
		return poisson0( rand, lambda );
	}
}


// EXPORTS //
export default factory;
